/**
 * Dashboard.
 *
 * Three quite different pages behind one route, because "what needs my attention"
 * means something different to each role: an employee wants their own day, a
 * manager wants their team's, HR wants the company's. The split is driven by
 * permissions rather than a role string, so a custom role lands somewhere sensible.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { get } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useActions } from '../components/actions.jsx';
import Celebrations from '../components/Celebrations.jsx';
import UpcomingLeave from '../components/UpcomingLeave.jsx';
import { useAttendanceDay } from '../lib/useAttendanceDay.js';
import { useWorkspaceView } from '../lib/workspaceView.jsx';
import {
  Card, Stat, PageHead, Table, Badge, StatusBadge, AttendanceStatusBadge, Person, Loading, ErrorNote,
  Icon, Insight, AiCard, Progress, Empty, CheckItem, Avatar,
} from '../components/ui.jsx';
import { LineChart, BarChart, HBars, Donut, Legend, token, seriesColor } from '../components/charts.jsx';
import {
  fmtDate, fmtDateShort, fmtTime, hhmm, inr, inrShort, isoDay, todayISO, dayOfWeek,
  relativeDays, pct, humanise, departmentLabel, attendanceWorkingMinutes, fmtWorkingHrs,
} from '../lib/format.js';

/* ------------------------------------------------------------------ shared bits */

function TodayCard({ onRegularise }) {
  const { user } = useAuth();
  const today = todayISO();
  const mine = useQuery({ queryKey: ['attendance', 'me'], queryFn: () => get('/attendance/me') });

  const record = (mine.data?.data ?? []).find((r) => isoDay(r.date) === today);
  const shift = user.shift;
  const worked = attendanceWorkingMinutes(record) ?? 0;
  const expected = 9 * 60;

  return (
    <Card>
      <div className="flex gap-14 wrap items-c">
        <div style={{ flex: 1, minWidth: 200 }}>
          <div className="txt-sm muted">Today · {dayOfWeek(today)}, {fmtDate(today)}</div>
          <div style={{ fontSize: 27, fontWeight: 750, letterSpacing: '-.04em', margin: '5px 0 3px' }}>
            {record?.firstInAt ? fmtTime(record.firstInAt) : '—'}
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-3)' }}> first punch</span>
          </div>
          <div className="flex items-c gap-6 wrap">
            {record ? (
              <AttendanceStatusBadge
                status={record.status}
                firstInAt={record.firstInAt}
                lastOutAt={record.lastOutAt}
                workMinutes={record.workMinutes}
                workMode={record.workMode}
                source={record.source}
              />
            ) : <Badge>Not marked yet</Badge>}
            {record?.workMode && (
              <Badge tone={record.workMode === 'WFH' ? 'info' : ''} icon={record.workMode === 'WFH' ? 'home' : 'building'}>
                {humanise(record.workMode)}
              </Badge>
            )}
            {record?.source && (
              <Badge icon={record.source === 'BIOMETRIC' ? 'fingerprint' : 'wifi'}>{humanise(record.source)}</Badge>
            )}
            {record?.lateMinutes > 0 && <Badge tone="warn">{record.lateMinutes}m late</Badge>}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="flex items-c" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <span className="txt-sm muted">Hours today</span>
            <span className="txt-sm" style={{ fontWeight: 650 }}>{hhmm(worked)} / 9h</span>
          </div>
          <Progress value={(worked / expected) * 100} />
          <div className="flex gap-6 mt-14">
            <button type="button" className="btn btn-sm" onClick={onRegularise}>
              <Icon name="edit" />Regularise
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

/** The day-by-day strip from the prototype: one block per working day. */
function AttendanceStrip({ records }) {
  const days = (records ?? [])
    .filter((r) => !['WEEKLY_OFF', 'HOLIDAY'].includes(r.status))
    .slice(-30);

  const colour = (r) => {
    if (r.status === 'PRESENT') return r.lateMinutes > 0 ? 'var(--warn)' : 'var(--good)';
    if (r.status === 'HALF_DAY') return 'var(--warn)';
    if (r.status === 'ABSENT') return 'var(--crit)';
    if (r.status === 'ON_LEAVE') return 'var(--s1)';
    return 'var(--surface-3)';
  };

  if (!days.length) return <Empty title="No attendance recorded yet" />;

  return (
    <>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {days.map((r) => {
          const hrs = fmtWorkingHrs(attendanceWorkingMinutes(r));
          const title = [
            fmtDate(r.date),
            humanise(r.status),
            r.firstInAt ? `${fmtTime(r.firstInAt)}–${r.lastOutAt ? fmtTime(r.lastOutAt) : '—'}` : null,
            hrs,
            r.lateMinutes > 0 ? `late ${r.lateMinutes}m` : null,
          ].filter(Boolean).join(' · ');
          return (
            <div
              key={r.id}
              title={title}
              style={{ width: 22, height: 30, borderRadius: 5, background: colour(r), opacity: 0.9 }}
            />
          );
        })}
      </div>
      <Legend items={[
        { label: 'On time', color: 'var(--good)' },
        { label: 'Late / half day', color: 'var(--warn)' },
        { label: 'Leave', color: 'var(--s1)' },
        { label: 'Absent', color: 'var(--crit)' },
      ]}
      />
    </>
  );
}

/* --------------------------------------------------------------- employee view */

function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const actions = useActions();

  const mine = useQuery({ queryKey: ['attendance', 'me'], queryFn: () => get('/attendance/me') });
  const balances = useQuery({ queryKey: ['leave', 'balances', user.id], queryFn: () => get(`/leave/balances/${user.id}`) });
  const myLeave = useQuery({ queryKey: ['leave', 'mine'], queryFn: () => get('/leave/requests', { scope: 'self', pageSize: 5 }) });
  const holidays = useQuery({ queryKey: ['holidays', 'upcoming'], queryFn: () => get('/reference/holidays/upcoming'), staleTime: 300_000 });
  const announcements = useQuery({ queryKey: ['announcements', 'feed'], queryFn: () => get('/content/announcements', { pageSize: 10 }) });
  const goals = useQuery({ queryKey: ['goals', 'mine'], queryFn: () => get('/performance/goals', { employeeId: user.id }) });
  const payslips = useQuery({ queryKey: ['payslips', 'me'], queryFn: () => get('/payroll/payslips/me') });

  const summary = mine.data?.meta?.summary;
  const needsAck = (announcements.data?.data ?? []).filter((a) => a.requiresAcknowledgement && !a.acknowledgedByMe);
  const myGoals = goals.data?.data ?? [];
  const goalAvg = myGoals.length ? Math.round(myGoals.reduce((a, g) => a + g.progress, 0) / myGoals.length) : null;
  const latestPay = payslips.data?.data?.[0];
  const totalLeave = (balances.data?.data ?? []).reduce((a, b) => a + b.available, 0);

  return (
    <>
      <PageHead
        title={`Good day, ${user.firstName}`}
        subtitle={`${user.designation} · signed in as ${humanise(user.role)}`}
        actions={(
          <>
            <button type="button" className="btn" onClick={actions.raiseTicket}><Icon name="ticket" />Raise a request</button>
            <button type="button" className="btn btn-primary" onClick={actions.applyLeave}><Icon name="plane" />Apply for leave</button>
          </>
        )}
      />

      <div className="grid g-4" style={{ marginBottom: 16 }}>
        <Stat
          label="Attendance (60d)" icon="clock"
          value={summary ? pct(summary.attendanceRate) : '—'}
          sub={summary ? `${summary.present} present · ${summary.absent} absent` : ''}
        />
        <Stat
          label="Leave balance" icon="plane" value={totalLeave} unit="days"
          sub={(balances.data?.data ?? []).map((b) => `${b.leaveType.code} ${b.available}`).join(' · ')}
        />
        <Stat
          label="Goal progress" icon="target" value={goalAvg === null ? '—' : pct(goalAvg)}
          sub={myGoals.length ? `${myGoals.filter((g) => g.status === 'ACHIEVED').length} of ${myGoals.length} achieved` : 'No goals set'}
        />
        <Stat
          label="Last net pay" icon="wallet" value={latestPay ? inr(latestPay.netPay) : '—'}
          sub={latestPay ? `${latestPay.period} · released ${fmtDateShort(latestPay.releasedAt)}` : 'No payslip yet'}
        />
      </div>

      <div className="grid g-32">
        <div className="grid" style={{ gap: 16 }}>
          <TodayCard onRegularise={() => actions.regularise()} />

          <Card
            title="My attendance" subtitle="Last 30 working days"
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/attendance')}>Open<Icon name="chevR" /></button>}
          >
            {mine.isLoading ? <Loading /> : <AttendanceStrip records={mine.data?.data} />}
          </Card>

          <Card
            title="My goals" subtitle={goals.data?.meta ? 'Current review cycle' : ''}
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/performance')}>All goals<Icon name="chevR" /></button>}
          >
            {myGoals.length === 0 ? <Empty icon="target" title="No goals set yet" sub="Your manager assigns these at the start of a cycle." /> : (
              myGoals.slice(0, 4).map((g) => (
                <div key={g.id} style={{ marginBottom: 14 }}>
                  <div className="flex items-c gap-10" style={{ marginBottom: 6 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.2 }}>{g.title}</div>
                    <div className="ml-auto"><StatusBadge value={g.status} /></div>
                  </div>
                  <div className="meter-row">
                    <Progress value={g.progress} tone={g.progress >= 100 ? 'var(--good)' : g.progress >= 60 ? 'var(--s1)' : 'var(--warn)'} />
                    <div className="mv">{g.progress}%</div>
                  </div>
                  <div className="txt-sm muted mt-8">Weight {g.weightPercent}% · due {fmtDate(g.dueDate)}</div>
                </div>
              ))
            )}
          </Card>
        </div>

        <div className="grid" style={{ gap: 16, alignContent: 'start' }}>
          {needsAck.length > 0 && (
            <Card title="Needs your acknowledgement" subtitle="Read and confirm to stay compliant" style={{ borderColor: 'var(--warn)' }}>
              {needsAck.map((a) => (
                <CheckItem
                  key={a.id}
                  title={a.title}
                  sub={`${a.category} · ${fmtDate(a.publishedAt)}`}
                  actions={<button type="button" className="btn btn-xs btn-primary" onClick={() => navigate('/announcements')}>Open</button>}
                />
              ))}
            </Card>
          )}

          <Card
            title="My leave requests" subtitle="Most recent"
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/leave')}>All<Icon name="chevR" /></button>}
            flush
          >
            <Table
              columns={[
                { key: 'type', label: 'Type', render: (r) => r.leaveType.name },
                { key: 'dates', label: 'Dates', render: (r) => `${fmtDateShort(r.fromDate)} – ${fmtDateShort(r.toDate)}` },
                { key: 'days', label: 'Days', numeric: true, render: (r) => r.days },
                { key: 'status', label: 'Status', render: (r) => <StatusBadge value={r.status} /> },
              ]}
              rows={(myLeave.data?.data ?? []).slice(0, 5)}
              empty="You have not applied for leave yet"
            />
          </Card>

          <Celebrations days={30} limit={4} />

          <Card title="Upcoming holidays" subtitle="Maharashtra calendar">
            {(holidays.data?.data ?? []).slice(0, 4).map((h) => (
              <div key={h.id} className="flex items-c gap-10" style={{ padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ textAlign: 'center', minWidth: 38 }}>
                  <div style={{ fontSize: 16, fontWeight: 750, lineHeight: 1 }}>{new Date(h.date).getUTCDate()}</div>
                  <div className="txt-sm muted">{fmtDateShort(h.date).split(' ')[1]}</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13.2 }}>{h.name}</div>
                  <div className="txt-sm muted">{dayOfWeek(h.date)} · {humanise(h.type)}</div>
                </div>
              </div>
            ))}
          </Card>

          <Card title="My leave balance" subtitle="Current financial year">
            {(balances.data?.data ?? []).map((b) => (
              <div key={b.id} style={{ marginBottom: 12 }}>
                <div className="flex items-c" style={{ justifyContent: 'space-between', fontSize: 12.8, marginBottom: 5 }}>
                  <span>{b.leaveType.name}</span>
                  <b>{b.available} / {b.entitled}</b>
                </div>
                <Progress thin value={b.entitled ? (b.available / b.entitled) * 100 : 0} tone={token(b.leaveType.colorToken)} />
              </div>
            ))}
          </Card>
        </div>
      </div>

      <ErrorNote error={mine.error ?? balances.error} />
    </>
  );
}

/* ---------------------------------------------------------------- manager view */

const STATUS_SORT = {
  PRESENT: 1,
  HALF_DAY: 2,
  PENDING_REGULARISATION: 3,
  ON_LEAVE: 4,
  WEEKLY_OFF: 5,
  HOLIDAY: 6,
  ABSENT: 7,
};

function dayStatusCell(cell) {
  if (!cell?.status) return <span className="muted">—</span>;
  return (
    <AttendanceStatusBadge
      status={cell.status}
      firstInAt={cell.firstInAt}
      lastOutAt={cell.lastOutAt}
      workMinutes={cell.workMinutes}
      workMode={cell.workMode}
      source={cell.source}
    />
  );
}

function dayTimeCell(value) {
  if (!value) return <span className="muted">—</span>;
  return <span className="mono">{fmtTime(value)}</span>;
}

function statusSortValue(cell) {
  if (!cell?.status) return null;
  return STATUS_SORT[cell.status] ?? 99;
}

function timeSortValue(value) {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/** One card: employee × today / yesterday / day-before — Manager, HR, Admin. */
function dayLooksWfh(day) {
  if (!day) return false;
  return day.workMode === 'WFH' || day.source === 'WEB_GEO';
}

function AttendanceDaysStrip() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Only HR/Admin may see the org-wide board; everyone else is self + team.
  const orgWideAttendance = user?.role === 'HR' || user?.role === 'ADMIN';
  const [filters, setFilters] = useState({
    q: '',
    branchId: '',
    departmentId: '',
    status: '',
    workMode: '', // '' = All · OFFICE = WFO · WFH
  });
  // Manager keys whose team is open. Empty = all collapsed (managers only).
  const [expandedManagers, setExpandedManagers] = useState(() => new Set());
  const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const toggleManager = (key) => {
    setExpandedManagers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const teamDays = useQuery({
    // Keyed by user so View-as / role switch never reuses another person's board.
    queryKey: ['attendance', 'team-days', user?.id],
    queryFn: () => get('/attendance/team-days', { days: 3 }),
    enabled: Boolean(user?.id),
  });
  const teamScope = useQuery({
    queryKey: ['employees', 'attendance-scope', user?.id],
    queryFn: () => get(`/employees/${user.id}/team`, { includeSelf: 1 }),
    enabled: Boolean(user?.id) && !orgWideAttendance,
    staleTime: 60_000,
  });
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => get('/reference/branches'),
    staleTime: 300_000,
  });
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => get('/reference/departments'),
    staleTime: 300_000,
  });
  const days = teamDays.data?.data?.days ?? [];
  const [today, yesterday, dayBefore] = days;

  /* Older APIs only return presentSample/summaries. Until that host is redeployed
     with day.rows, fill the table from the daily register endpoints. */
  const needsRegisterFallback = !teamDays.isLoading
    && days.length > 0
    && days.every((d) => !Array.isArray(d.rows));

  const registers = useQueries({
    queries: days.map((d) => ({
      queryKey: ['attendance', 'dash-register', user?.id, d.date],
      queryFn: () => get('/attendance', { date: d.date, pageSize: 500 }),
      enabled: needsRegisterFallback && Boolean(d.date) && Boolean(user?.id),
    })),
  });

  const registersLoading = needsRegisterFallback && registers.some((q) => q.isLoading);
  const registersError = needsRegisterFallback && registers.some((q) => q.isError);

  // null = org-wide (no filter). undefined = team scope still loading.
  const scopeIds = useMemo(() => {
    if (orgWideAttendance) return null;
    if (teamScope.isLoading) return undefined;
    const ids = new Set((teamScope.data?.data ?? []).map((e) => e.id));
    if (user?.id) ids.add(user.id);
    return ids;
  }, [orgWideAttendance, teamScope.isLoading, teamScope.data, user?.id]);

  const rows = useMemo(() => {
    if (scopeIds === undefined) return [];
    const byId = new Map();
    const ingest = (di, list) => {
      for (const r of list) {
        const employeeId = r.employeeId || r.employee?.id;
        if (!employeeId) continue;
        if (scopeIds && !scopeIds.has(employeeId)) continue;
        if (!byId.has(employeeId)) {
          byId.set(employeeId, {
            id: employeeId,
            employeeId,
            fullName: r.fullName || r.employee?.fullName || null,
            employeeCode: r.employeeCode || r.employee?.employeeCode || null,
            dateOfJoining: r.dateOfJoining || r.employee?.dateOfJoining || null,
            branchId: r.branchId || r.employee?.branch?.id || null,
            branch: r.branch || r.employee?.branch?.shortName || null,
            departmentId: r.departmentId || r.employee?.department?.id || null,
            department: r.department || r.employee?.department?.name || null,
            managerId: r.managerId || r.employee?.managerId || r.employee?.manager?.id || null,
            managerName: r.managerName || r.employee?.manager?.fullName || null,
            profileWorkMode: r.employee?.workMode || r.workMode || null,
            today: null,
            yesterday: null,
            dayBefore: null,
          });
        }
        const row = byId.get(employeeId);
        const fullName = r.fullName || r.employee?.fullName;
        const employeeCode = r.employeeCode || r.employee?.employeeCode;
        const dateOfJoining = r.dateOfJoining || r.employee?.dateOfJoining;
        const branchId = r.branchId || r.employee?.branch?.id;
        const branch = r.branch || r.employee?.branch?.shortName;
        const departmentId = r.departmentId || r.employee?.department?.id;
        const department = r.department || r.employee?.department?.name;
        const managerId = r.managerId || r.employee?.managerId || r.employee?.manager?.id;
        const managerName = r.managerName || r.employee?.manager?.fullName;
        const profileWorkMode = r.employee?.workMode || r.workMode;
        if (!row.fullName && fullName) row.fullName = fullName;
        if (!row.employeeCode && employeeCode) row.employeeCode = employeeCode;
        if (!row.dateOfJoining && dateOfJoining) row.dateOfJoining = dateOfJoining;
        if (!row.branchId && branchId) row.branchId = branchId;
        if (!row.branch && branch) row.branch = branch;
        if (!row.departmentId && departmentId) row.departmentId = departmentId;
        if (!row.department && department) row.department = department;
        if (!row.managerId && managerId) row.managerId = managerId;
        if (!row.managerName && managerName) row.managerName = managerName;
        if (!row.profileWorkMode && profileWorkMode) row.profileWorkMode = profileWorkMode;
        const key = di === 0 ? 'today' : di === 1 ? 'yesterday' : 'dayBefore';
        row[key] = {
          status: r.status,
          firstInAt: r.firstInAt,
          lastOutAt: r.lastOutAt,
          workMinutes: r.workMinutes,
          workMode: r.workMode ?? r.employee?.workMode,
          source: r.source,
        };
      }
    };

    if (needsRegisterFallback) {
      registers.forEach((q, di) => ingest(di, q.data?.data ?? []));
    } else {
      days.forEach((day, di) => ingest(di, day.rows ?? []));
    }

    return [...byId.values()].sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
  }, [days, needsRegisterFallback, registers, scopeIds]);

  // Parent department chips should include nested teams (same as /attendance API).
  const departmentFilterIds = useMemo(() => {
    if (!filters.departmentId) return null;
    const list = departments.data?.data ?? [];
    const ids = new Set([filters.departmentId]);
    for (const d of list) {
      if (d.parentId === filters.departmentId) ids.add(d.id);
    }
    return ids;
  }, [filters.departmentId, departments.data]);

  const filteredRows = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.branchId && r.branchId !== filters.branchId) return false;
      if (departmentFilterIds && !departmentFilterIds.has(r.departmentId)) return false;
      if (filters.status && r.today?.status !== filters.status) return false;
      if (filters.workMode) {
        const wfh = dayLooksWfh(r.today) || (!r.today && r.profileWorkMode === 'WFH');
        if (filters.workMode === 'WFH' && !wfh) return false;
        if (filters.workMode === 'OFFICE' && wfh) return false;
      }
      if (q) {
        const hay = `${r.fullName || ''} ${r.employeeCode || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filters, departmentFilterIds]);

  // Counts follow the active department/branch/search filter so the chips match
  // the table (filtering Software then still showing org-wide totals looked wrong).
  const modeCountSource = useMemo(() => {
    if (!filters.departmentId && !filters.branchId && !filters.q.trim()) return rows;
    return filteredRows;
  }, [rows, filteredRows, filters.departmentId, filters.branchId, filters.q]);

  const modeCounts = useMemo(() => {
    let wfh = 0;
    let office = 0;
    for (const r of modeCountSource) {
      if (dayLooksWfh(r.today) || (!r.today && r.profileWorkMode === 'WFH')) wfh += 1;
      else office += 1;
    }
    return { all: modeCountSource.length, wfh, office };
  }, [modeCountSource]);

  // Search / department / branch / status / mode: flat list so people stay
  // visible without expanding manager groups (collapsed groups looked like
  // "only 2 employees"). Otherwise manager → expand team.
  const flattenAttendance = Boolean(
    filters.q.trim()
    || filters.departmentId
    || filters.branchId
    || filters.status
    || filters.workMode,
  );
  const groupedRows = useMemo(
    () => (flattenAttendance
      ? filteredRows
      : groupAttendanceByManager(filteredRows, { roster: rows, expanded: expandedManagers })),
    [filteredRows, rows, expandedManagers, flattenAttendance],
  );

  const subtitle = [today, yesterday, dayBefore]
    .filter(Boolean)
    .map((d, i) => `${['Today', 'Yesterday', 'Day before'][i]} ${fmtDateShort(d.date)}`)
    .join(' · ') || 'Today · yesterday · day before';

  const loading = teamDays.isLoading || registersLoading || (!orgWideAttendance && teamScope.isLoading);
  const errored = teamDays.isError || registersError || (!orgWideAttendance && teamScope.isError);
  const filterActive = Boolean(filters.q || filters.branchId || filters.departmentId || filters.status || filters.workMode);

  return (
    <Card
      title="Attendance"
      subtitle={subtitle}
      actions={(
        <button type="button" className="btn btn-sm" onClick={() => navigate('/attendance')}>
          Open register<Icon name="chevR" />
        </button>
      )}
      style={{ marginBottom: 16 }}
    >
      <div className="att-mode-tabs" role="tablist" aria-label="Work mode">
        {[
          { id: '', label: 'All', count: modeCounts.all },
          { id: 'OFFICE', label: 'WFO', count: modeCounts.office },
          { id: 'WFH', label: 'WFH', count: modeCounts.wfh },
        ].map((t) => (
          <button
            key={t.id || 'all'}
            type="button"
            role="tab"
            aria-selected={filters.workMode === t.id}
            className={`att-mode-tab${filters.workMode === t.id ? ' on' : ''}`}
            onClick={() => setFilter({ workMode: t.id })}
          >
            {t.label}
            <span className="cnt">{t.count}</span>
          </button>
        ))}
      </div>
      <div className="flex gap-10 wrap items-c" style={{ marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <input
            className="inp"
            placeholder="Search employee…"
            value={filters.q}
            onChange={(e) => setFilter({ q: e.target.value })}
            aria-label="Search employee"
          />
        </div>
        <select
          className="inp"
          style={{ width: 'auto' }}
          value={filters.branchId}
          onChange={(e) => setFilter({ branchId: e.target.value })}
          aria-label="Branch"
        >
          <option value="">All branches</option>
          {(branches.data?.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>{b.shortName}</option>
          ))}
        </select>
        <select
          className="inp"
          style={{ width: 'auto' }}
          value={filters.departmentId}
          onChange={(e) => setFilter({ departmentId: e.target.value })}
          aria-label="Department"
        >
          <option value="">All departments</option>
          {(departments.data?.data ?? []).map((d) => (
            <option key={d.id} value={d.id}>{departmentLabel(d, departments.data?.data ?? [])}</option>
          ))}
        </select>
        <select
          className="inp"
          style={{ width: 'auto' }}
          value={filters.status}
          onChange={(e) => setFilter({ status: e.target.value })}
          aria-label="Today status"
        >
          <option value="">All statuses</option>
          {['PRESENT', 'ABSENT', 'ON_LEAVE', 'HALF_DAY', 'WEEKLY_OFF', 'HOLIDAY', 'PENDING_REGULARISATION'].map((v) => (
            <option key={v} value={v}>{humanise(v)}</option>
          ))}
        </select>
        {filterActive && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setFilters({ q: '', branchId: '', departmentId: '', status: '', workMode: '' })}
          >
            Clear
          </button>
        )}
      </div>
      {loading ? <Loading /> : errored ? (
        <div className="txt-sm" style={{ color: 'var(--crit)' }}>
          Could not load attendance days. Check that biometric punches are syncing.
        </div>
      ) : (
        <div style={{ maxHeight: 440, overflow: 'auto' }}>
          <Table
            empty={filterActive ? 'No employees match these filters' : 'No employees in scope'}
            emptySub={filterActive ? 'Try clearing branch, department, status, or search.' : 'Attendance rows appear once people are expected on shift.'}
            onRowClick={(r) => {
              if (r?.employeeId) navigate(`/employee/${r.employeeId}`);
            }}
            columns={[
              {
                key: 'name',
                label: 'Employee',
                render: (r) => (r.__kind === 'group' ? (
                  <div className="tbl-mgr-row">
                    <button
                      type="button"
                      className="tbl-mgr-chev-btn"
                      aria-expanded={r.expanded}
                      aria-label={r.expanded ? 'Collapse team' : 'Expand team'}
                      onClick={(e) => { e.stopPropagation(); toggleManager(r.groupKey); }}
                    >
                      <Icon name={r.expanded ? 'chevD' : 'chevR'} className="tbl-mgr-chev" />
                    </button>
                    <Person
                      name={r.fullName || r.label || '—'}
                      department={r.department}
                      dateOfJoining={r.dateOfJoining}
                      employeeId={r.employeeId}
                    />
                    <span className="tbl-group-cnt">{r.count}</span>
                  </div>
                ) : (
                  <div className={r.__nested ? 'tbl-emp-nest' : undefined}>
                    <Person
                      name={r.fullName || '—'}
                      department={r.department}
                      dateOfJoining={r.dateOfJoining}
                      employeeId={r.employeeId}
                    />
                  </div>
                )),
              },
              {
                key: 'id',
                label: 'ID',
                render: (r) => <span className="mono">{r.employeeCode || '—'}</span>,
              },
              {
                key: 'branch',
                label: 'Branch',
                render: (r) => r.branch || '—',
              },
              {
                key: 'today',
                label: 'Today',
                render: (r) => dayStatusCell(r.today),
              },
              {
                key: 'todayIn',
                label: 'In',
                numeric: true,
                render: (r) => dayTimeCell(r.today?.firstInAt),
              },
              {
                key: 'todayOut',
                label: 'Out',
                numeric: true,
                render: (r) => dayTimeCell(r.today?.lastOutAt),
              },
              {
                key: 'yesterday',
                label: 'Yesterday',
                render: (r) => dayStatusCell(r.yesterday),
              },
              {
                key: 'yesterdayIn',
                label: 'In',
                numeric: true,
                render: (r) => dayTimeCell(r.yesterday?.firstInAt),
              },
              {
                key: 'yesterdayOut',
                label: 'Out',
                numeric: true,
                render: (r) => dayTimeCell(r.yesterday?.lastOutAt),
              },
              {
                key: 'dayBefore',
                label: 'Day before',
                render: (r) => dayStatusCell(r.dayBefore),
              },
              {
                key: 'dayBeforeIn',
                label: 'In',
                numeric: true,
                render: (r) => dayTimeCell(r.dayBefore?.firstInAt),
              },
              {
                key: 'dayBeforeOut',
                label: 'Out',
                numeric: true,
                render: (r) => dayTimeCell(r.dayBefore?.lastOutAt),
              },
            ]}
            rows={groupedRows}
          />
        </div>
      )}
    </Card>
  );
}

/**
 * Manager header rows carry the manager’s own attendance; nested reports only
 * appear while that manager key is in `expanded`.
 */
function groupAttendanceByManager(employees, { roster = [], expanded = new Set() } = {}) {
  const rosterById = new Map(roster.map((r) => [r.employeeId || r.id, r]));
  const buckets = new Map();
  for (const r of employees) {
    const key = r.managerId || '__none__';
    if (!buckets.has(key)) {
      buckets.set(key, {
        id: key,
        managerId: r.managerId || null,
        label: r.managerName || 'No reporting manager',
        people: [],
      });
    }
    const bucket = buckets.get(key);
    if (!bucket.managerId && r.managerId) bucket.managerId = r.managerId;
    if (r.managerName && bucket.label === 'No reporting manager') bucket.label = r.managerName;
    bucket.people.push(r);
  }

  const groups = [...buckets.values()].sort((a, b) => {
    if (a.id === '__none__') return 1;
    if (b.id === '__none__') return -1;
    return a.label.localeCompare(b.label);
  });

  const out = [];
  for (const g of groups) {
    g.people.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
    const reports = g.people.filter((p) => p.employeeId !== g.managerId);
    const managerRow = g.managerId ? rosterById.get(g.managerId) : null;
    const isOpen = expanded.has(g.id);
    out.push({
      __kind: 'group',
      __asRow: true,
      id: `mgr:${g.id}`,
      groupKey: g.id,
      expanded: isOpen,
      count: reports.length,
      label: managerRow?.fullName || g.label,
      employeeId: managerRow?.employeeId || g.managerId || null,
      fullName: managerRow?.fullName || g.label,
      employeeCode: managerRow?.employeeCode || null,
      department: managerRow?.department || null,
      dateOfJoining: managerRow?.dateOfJoining || null,
      branch: managerRow?.branch || null,
      today: managerRow?.today || null,
      yesterday: managerRow?.yesterday || null,
      dayBefore: managerRow?.dayBefore || null,
    });
    if (isOpen) {
      for (const person of reports) out.push({ ...person, __nested: true });
    }
  }
  return out;
}

function ManagerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const team = useQuery({ queryKey: ['team', user.id], queryFn: () => get(`/employees/${user.id}/team`) });
  const teamDays = useQuery({
    queryKey: ['attendance', 'team-days', user.id],
    queryFn: () => get('/attendance/team-days', { days: 3 }),
  });
  const pending = useQuery({ queryKey: ['leave', 'pending'], queryFn: () => get('/leave/requests', { status: 'PENDING', pageSize: 50 }) });
  const regs = useQuery({ queryKey: ['regularisations', 'pending'], queryFn: () => get('/attendance/regularisations', { status: 'PENDING' }) });
  const trend = useQuery({ queryKey: ['reports', 'attendance'], queryFn: () => get('/reports/attendance') });
  const risk = useQuery({ queryKey: ['attrition'], queryFn: () => get('/insights/attrition') });
  /* Asked unconditionally. Somebody who heads no department gets an empty
     answer rather than a refusal, so the panel below simply does not appear —
     no permission check duplicated on this side, and nothing to keep in step
     with the server's idea of who qualifies. */
  const teamCost = useQuery({ queryKey: ['reports', 'team-cost'], queryFn: () => get('/reports/team-cost') });

  const todayDate = teamDays.data?.data?.days?.[0]?.date ?? todayISO();
  const register = useQuery({
    queryKey: ['attendance', 'register', user.id, todayDate],
    queryFn: () => get('/attendance', { date: todayDate, pageSize: 12 }),
    enabled: Boolean(todayDate),
  });

  const days = teamDays.data?.data?.days ?? [];
  const todaySummary = days[0];
  const members = team.data?.data ?? [];
  const risky = (risk.data?.data?.results ?? []).filter((r) => r.band !== 'LOW').slice(0, 4);
  const trendPoints = trend.data?.data?.trend ?? [];
  const avgRating = members.length
    ? (members.reduce((a, e) => a + (e.lastRating ?? 0), 0) / members.length).toFixed(2)
    : '—';

  return (
    <>
      <PageHead
        title={`Good day, ${user.firstName}`}
        subtitle={`${members.length} people reporting into you · ${user.designation}`}
        actions={(
          <>
            <button type="button" className="btn" onClick={() => navigate('/reports')}><Icon name="chart" />Team report</button>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/leave')}>
              <Icon name="check" />Review approvals ({(pending.data?.data?.length ?? 0) + (regs.data?.data?.length ?? 0)})
            </button>
          </>
        )}
      />

      <AttendanceDaysStrip />

      <div className="grid g-5" style={{ marginBottom: 16 }}>
        <Stat
          label="Present today" icon="users"
          value={todaySummary?.checkedIn ?? todaySummary?.present ?? '—'} unit={todaySummary ? `/ ${todaySummary.total}` : ''}
          sub={todaySummary ? `${todaySummary.office} office · ${todaySummary.wfh} WFH` : ''}
        />
        <Stat label="On leave" icon="plane" value={todaySummary?.onLeave ?? '—'} sub={todaySummary ? `${todaySummary.absent} unplanned absent` : ''} />
        <Stat label="Late marks" icon="clock" value={todaySummary?.late ?? '—'} sub="Beyond the grace period" />
        <Stat
          label="Pending approvals" icon="clipboard"
          value={(pending.data?.data?.length ?? 0) + (regs.data?.data?.length ?? 0)}
          sub={`${pending.data?.data?.length ?? 0} leave · ${regs.data?.data?.length ?? 0} regularisation`}
        />
        <Stat label="Avg team rating" icon="star" value={avgRating} sub="Last review cycle" />
      </div>

      {/* Only for somebody who heads a department. Totals, never a figure
          against a name: what the department costs is a head's business, what
          each person earns is not. */}
      {(teamCost.data?.data?.headsDepartments?.length ?? 0) > 0 && (
        <Card
          title="What your department costs"
          subtitle={`${teamCost.data.data.headsDepartments.join(' · ')} · monthly gross as it stands today`}
          style={{ marginBottom: 16 }}
        >
          <div className="grid g-3" style={{ marginBottom: 12 }}>
            <Stat
              label="Monthly gross" icon="wallet"
              value={inrShort(teamCost.data.data.grossMonthly)}
              sub={`${teamCost.data.data.headcount} people`}
            />
            <Stat
              label="Cost per head" icon="users"
              value={teamCost.data.data.headcount ? inrShort(teamCost.data.data.perHead) : '—'}
              sub="Across the department"
            />
            <Stat
              label="Departments" icon="briefcase"
              value={teamCost.data.data.departments.length}
              sub="You are head of"
            />
          </div>
          {teamCost.data.data.departments.length > 1 && (
            <Table
              columns={[
                { key: 'name', label: 'Department' },
                { key: 'headcount', label: 'People' },
                { key: 'gross', label: 'Monthly', render: (r) => inrShort(r.gross) },
                { key: 'per', label: 'Per head', render: (r) => (r.headcount ? inrShort(Math.round(r.gross / r.headcount)) : '—') },
              ]}
              rows={teamCost.data.data.departments}
              empty="Nothing in scope"
            />
          )}
          <div className="txt-sm muted mt-8">
            Totals only. What an individual earns stays with them, HR and an administrator.
          </div>
        </Card>
      )}

      <div className="grid g-32">
        <div className="grid" style={{ gap: 16 }}>
          <Card title="Attendance rate" subtitle="Share present, recent working days">
            {trendPoints.length > 1 ? (
              <>
                <LineChart
                  height={210}
                  series={[{ name: 'Attendance', data: trendPoints.map((p) => p.attendanceRate), color: 'var(--s1)', area: true }]}
                  labels={trendPoints.map((p) => fmtDateShort(p.date))}
                  format={(v) => `${v}%`}
                  dots={false}
                />
                <Legend items={[{ label: 'Attendance %', color: 'var(--s1)' }]} />
              </>
            ) : <Loading />}
          </Card>

          <Card
            title="Who is in today"
            subtitle={`${members.length} people in your scope · unmarked count as absent`}
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/attendance')}>Full register<Icon name="chevR" /></button>}
            flush
          >
            <Table
              onRowClick={(r) => navigate(`/employee/${r.employeeId}`)}
              columns={[
                {
                  key: 'employee',
                  label: 'Employee',
                  render: (r) => (
                    <Person
                      name={r.employee.fullName}
                      department={r.employee.department}
                      dateOfJoining={r.employee.dateOfJoining}
                      employeeId={r.employee.id}
                      hasPhoto={Boolean(r.employee.photoUrl)}
                    />
                  ),
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (r) => (
                    <AttendanceStatusBadge
                      status={r.status}
                      firstInAt={r.firstInAt}
                      lastOutAt={r.lastOutAt}
                      workMinutes={r.workMinutes}
                      workMode={r.workMode}
                      source={r.source}
                    />
                  ),
                },
                { key: 'in', label: 'In', render: (r) => (r.firstInAt ? (<>{fmtTime(r.firstInAt)}{r.lateMinutes > 0 && <> <Badge tone="warn">+{r.lateMinutes}m</Badge></>}</>) : '—') },
                { key: 'mode', label: 'Mode', render: (r) => <Badge tone={r.workMode === 'WFH' ? 'info' : ''}>{humanise(r.workMode)}</Badge> },
                { key: 'worked', label: 'Hours', numeric: true, render: (r) => hhmm(attendanceWorkingMinutes(r)) },
              ]}
              rows={register.data?.data ?? []}
              empty="No people in your team scope"
            />
          </Card>
        </div>

        <div className="grid" style={{ gap: 16, alignContent: 'start' }}>
          <Card
            title="Awaiting your approval"
            subtitle={`${pending.data?.data?.length ?? 0} leave · ${regs.data?.data?.length ?? 0} regularisation`}
          >
            {(pending.data?.data ?? []).slice(0, 5).map((l) => (
              <CheckItem
                key={l.id}
                title={`${l.employee.fullName} · ${l.leaveType.name}`}
                sub={`${fmtDateShort(l.fromDate)} – ${fmtDateShort(l.toDate)} · ${l.days}d · ${l.reason}`}
                actions={<button type="button" className="btn btn-xs" onClick={() => navigate('/leave')}>Review</button>}
              />
            ))}
            {(pending.data?.data?.length ?? 0) === 0 && (
              <Empty icon="check" title="All clear" sub="No approvals waiting in your scope." />
            )}
          </Card>

          <AiCard
            tag="AI insight"
            title="Retention signals worth a conversation"
            actions={<button type="button" className="btn btn-xs" onClick={() => navigate('/insights')}>See all</button>}
          >
            {risky.length === 0
              ? <div className="txt-sm muted">No elevated risk signals in your scope right now.</div>
              : risky.map((r) => (
                <Insight key={r.employee.id} plain>
                  <div className="flex items-c gap-10">
                    <Avatar name={r.employee.fullName} size="sm" />
                    <div style={{ minWidth: 0 }}>
                      <div className="ins-t">
                        {r.employee.fullName}{' '}
                        <Badge tone={r.band === 'HIGH' ? 'crit' : 'warn'}>{humanise(r.band)} risk · {r.score}</Badge>
                      </div>
                      <div className="ins-d">{r.factors?.[0]}</div>
                    </div>
                  </div>
                </Insight>
              ))}
            <div className="txt-sm muted mt-8">
              Reads attendance, tenure, salary revision gaps, leave clustering and goal progress. Advisory, not a decision.
            </div>
          </AiCard>

          <UpcomingLeave days={21} title="Who is off next" />

          <Celebrations days={30} limit={5} />
        </div>
      </div>

      <ErrorNote error={team.error || teamDays.error} />
    </>
  );
}

/* --------------------------------------------------------------------- HR view */

function HrDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const actions = useActions();

  const day = useAttendanceDay();
  const headcount = useQuery({ queryKey: ['reports', 'headcount'], queryFn: () => get('/reports/headcount') });
  const turnover = useQuery({ queryKey: ['reports', 'turnover'], queryFn: () => get('/reports/turnover') });
  const attendance = useQuery({ queryKey: ['reports', 'attendance'], queryFn: () => get('/reports/attendance') });
  const pending = useQuery({ queryKey: ['leave', 'pending'], queryFn: () => get('/leave/requests', { status: 'PENDING', pageSize: 50 }) });
  const anomalies = useQuery({ queryKey: ['anomalies'], queryFn: () => get('/insights/anomalies') });
  const devices = useQuery({ queryKey: ['devices'], queryFn: () => get('/biometric/devices') });
  const checklists = useQuery({ queryKey: ['checklists', 'onboarding'], queryFn: () => get('/checklists', { kind: 'ONBOARDING' }) });
  const announcements = useQuery({ queryKey: ['announcements', 'feed'], queryFn: () => get('/content/announcements', { pageSize: 4 }) });

  const s = day.summary;
  const hc = headcount.data?.data;
  const tv = turnover.data?.data;
  const trendPoints = attendance.data?.data?.trend ?? [];
  const modes = hc?.byWorkMode ?? [];

  return (
    <>
      <PageHead
        title="People Dashboard"
        subtitle={hc ? `${hc.total} active employees · ${dayOfWeek(todayISO())}, ${fmtDate(todayISO())}` : 'Loading…'}
        actions={(
          <>
            <button type="button" className="btn" onClick={() => navigate('/reports')}><Icon name="chart" />Reports</button>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/directory')}><Icon name="userPlus" />Directory</button>
          </>
        )}
      />

      <AttendanceDaysStrip />

      <div className="grid g-5" style={{ marginBottom: 16 }}>
        <Stat label="Headcount" icon="users" value={hc?.total ?? '—'} sub={hc ? `${hc.peopleManagers} people managers` : ''} />
        <Stat
          label={day.isToday ? 'Present today' : `Present ${day.label}`} icon="clock"
          value={s?.checkedIn ?? s?.present ?? '—'} unit={s ? `/ ${s.total}` : ''}
          sub={s ? [
            `${s.office} office · ${s.wfh} WFH · ${s.late} late`,
            s.weeklyOff ? `${s.weeklyOff} weekly off` : null,
            s.holiday ? `${s.holiday} holiday` : null,
          ].filter(Boolean).join(' · ') : ''}
        />
        <Stat
          label={day.isToday ? 'On leave today' : `On leave ${day.label}`} icon="plane"
          value={s?.onLeave ?? '—'} sub={s ? `${s.absent} unplanned absent` : ''}
        />
        <Stat label="Attrition (12m)" icon="trendDn" value={tv ? `${tv.attritionRate}%` : '—'} sub={tv ? `${tv.exitCount} exits · ${tv.voluntary} voluntary` : ''} />
        <Stat label="Open approvals" icon="clipboard" value={pending.data?.data?.length ?? '—'} sub="Leave awaiting a decision" />
      </div>

      <div className="grid g-32" style={{ marginBottom: 16 }}>
        <Card title="Attendance rate" subtitle="Share of expected employees present">
          {trendPoints.length > 1 ? (
            <>
              <LineChart
                height={236}
                series={[{ name: 'Attendance', data: trendPoints.map((p) => p.attendanceRate), color: 'var(--s1)', area: true }]}
                labels={trendPoints.map((p) => fmtDateShort(p.date))}
                format={(v) => `${v}%`}
                dots={false}
                xTicks={10}
              />
              <Legend items={[{ label: 'Company attendance %', color: 'var(--s1)' }, { label: 'Target 92%' }]} />
            </>
          ) : <Loading />}
        </Card>

        <Card title="Work mode split" subtitle="How the company works">
          {modes.length ? (
            <div className="center">
              <Donut
                data={modes.map((m, i) => ({ l: humanise(m.mode), v: m.count, c: seriesColor(i) }))}
                middle={hc?.total}
                middleLabel="Employees"
              />
              <Legend items={modes.map((m, i) => ({ label: `${humanise(m.mode)} · ${m.count}`, color: seriesColor(i) }))} />
            </div>
          ) : <Loading />}
        </Card>
      </div>

      <div className="grid g-3" style={{ marginBottom: 16 }}>
        <Card title="Headcount by department" subtitle="Active employees">
          {hc ? <HBars showTotal rows={hc.byDepartment.map((d) => ({ l: d.name, v: d.count, c: token(d.colorToken) }))} /> : <Loading />}
        </Card>
        <Card title="Headcount by branch" subtitle="Including remote staff at their home branch">
          {hc ? <HBars showTotal rows={hc.byBranch.map((b, i) => ({ l: b.shortName, v: b.count, c: seriesColor(i) }))} /> : <Loading />}
        </Card>
        <Card title="Tenure" subtitle={hc ? `Average ${hc.averageTenureYears} years` : ''}>
          {hc ? <HBars showTotal rows={hc.byTenure.map((t, i) => ({ l: t.label, v: t.count, c: seriesColor(i) }))} /> : <Loading />}
        </Card>
      </div>

      <div className="grid g-32">
        <div className="grid" style={{ gap: 16 }}>
          <Card
            title="Approvals queue" subtitle="Leave waiting on managers and HR"
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/leave')}>Open leave<Icon name="chevR" /></button>}
            flush
          >
            <Table
              onRowClick={(r) => navigate(`/employee/${r.employeeId}`)}
              columns={[
                {
                  key: 'employee',
                  label: 'Employee',
                  render: (r) => (
                    <Person
                      name={r.employee.fullName}
                      department={r.employee.department}
                      dateOfJoining={r.employee.dateOfJoining}
                      employeeId={r.employee.id}
                      hasPhoto={Boolean(r.employee.photoUrl)}
                    />
                  ),
                },
                { key: 'type', label: 'Type', render: (r) => r.leaveType.name },
                { key: 'dates', label: 'Dates', render: (r) => `${fmtDateShort(r.fromDate)} – ${fmtDateShort(r.toDate)}` },
                { key: 'days', label: 'Days', numeric: true, render: (r) => r.days },
                { key: 'waiting', label: 'Waiting', render: (r) => relativeDays(r.appliedAt) },
                { key: 'approver', label: 'Approver', render: (r) => r.approver?.fullName ?? '—' },
              ]}
              rows={(pending.data?.data ?? []).slice(0, 7)}
              empty="Nothing waiting"
            />
          </Card>

          <Card
            title="Onboarding in progress" subtitle={`${checklists.data?.data?.length ?? 0} new joiners`}
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/onboarding')}>Open<Icon name="chevR" /></button>}
            flush
          >
            <Table
              onRowClick={(r) => navigate(`/employee/${r.employee.id}`)}
              columns={[
                {
                  key: 'person',
                  label: 'New joiner',
                  render: (r) => (
                    <Person
                      name={r.employee.fullName}
                      department={r.employee.department}
                      dateOfJoining={r.employee.dateOfJoining}
                      employeeId={r.employee.id}
                      hasPhoto={Boolean(r.employee.photoUrl)}
                    />
                  ),
                },
                { key: 'dept', label: 'Department', render: (r) => r.employee.department?.name ?? '—' },
                { key: 'doj', label: 'Joining date', render: (r) => fmtDate(r.employee.dateOfJoining) },
                {
                  key: 'progress',
                  label: 'Checklist',
                  render: (r) => (
                    <div className="meter-row" style={{ minWidth: 150 }}>
                      <Progress value={r.percent} />
                      <div className="mv">{r.done}/{r.total}</div>
                    </div>
                  ),
                },
                { key: 'blockers', label: 'Blockers', render: (r) => (r.overdue ? <Badge tone="crit">{r.overdue} overdue</Badge> : <Badge tone="good">On track</Badge>) },
              ]}
              rows={checklists.data?.data ?? []}
              empty="Nobody is onboarding right now"
            />
          </Card>
        </div>

        <div className="grid" style={{ gap: 16, alignContent: 'start' }}>
          <AiCard
            tag="AI briefing"
            title="What needs your attention today"
            actions={<button type="button" className="btn btn-xs" onClick={() => navigate('/insights')}>All insights</button>}
          >
            <div className="txt-sm muted mb-10">Derived from attendance, leave, payroll and ticket data</div>
            {(anomalies.data?.data ?? []).slice(0, 4).map((a, i) => (
              <Insight
                key={i}
                icon={a.severity === 'CRITICAL' ? 'alert' : a.category === 'Attendance' ? 'clock' : 'info'}
                tone={a.severity === 'CRITICAL' ? 'crit' : a.severity === 'WARNING' ? 'warn' : 'info'}
                title={a.title}
                detail={a.detail}
              />
            ))}
            {(anomalies.data?.data ?? []).length === 0 && <div className="txt-sm muted">Nothing flagged right now.</div>}
          </AiCard>

          <Card
            title="Biometric device health"
            subtitle={devices.data ? `${devices.data.data.filter((d) => d.isActive !== false && (d.healthStatus || d.status) === 'ONLINE').length} of ${devices.data.data.filter((d) => d.isActive !== false).length} online` : ''}
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/biometric')}>Manage<Icon name="chevR" /></button>}
          >
            {(devices.data?.data ?? []).filter((d) => d.isActive !== false).map((d) => {
              const tone = (d.healthStatus || d.status) === 'ONLINE' ? 'good' : (d.healthStatus || d.status) === 'DEGRADED' ? 'warn' : 'crit';
              return (
                <div key={d.id} className="flex items-c gap-10" style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div className="ins-ico" style={{ background: `var(--${tone}-bg)`, color: `var(--${tone})` }}>
                    <Icon name="fingerprint" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{d.code} · {d.branch?.shortName}</div>
                    <div className="txt-sm muted">{d.model}</div>
                  </div>
                  <div className="ml-auto"><StatusBadge value={d.healthStatus || d.status} /></div>
                </div>
              );
            })}
          </Card>

          <Card
            title="Latest announcements"
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/announcements')}>All<Icon name="chevR" /></button>}
          >
            {(announcements.data?.data ?? []).map((a) => (
              <div key={a.id} style={{ padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                <div className="flex items-c gap-6 mb-10">
                  <Badge tone={a.priority === 'HIGH' ? 'warn' : 'info'}>{a.category}</Badge>
                  {a.isPinned && <Badge>Pinned</Badge>}
                  <span className="txt-sm muted ml-auto">{fmtDateShort(a.publishedAt)}</span>
                </div>
                <div style={{ fontWeight: 600, fontSize: 13.2 }}>{a.title}</div>
                {a.requiresAcknowledgement && (
                  <div className="txt-sm muted mt-8">{a.acknowledgementPercent}% acknowledged</div>
                )}
              </div>
            ))}
          </Card>

          <UpcomingLeave days={21} title="Who is off next" />

          <Celebrations days={30} limit={6} />
        </div>
      </div>

      <ErrorNote error={headcount.error} />
    </>
  );
}

/* ---------------------------------------------------------------- management */

/** The last N months as "YYYY-MM", newest last. */
function recentPeriods(count = 24) {
  const out = [];
  const d = new Date();
  for (let i = count - 1; i >= 0; i -= 1) {
    const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
    out.push(`${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

const monthLabel = (period) => {
  if (!period) return '';
  const [y, m] = period.split('-');
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1]} ${y.slice(2)}`;
};

/**
 * The leadership view: what the company costs, and where the cost sits.
 *
 * Built around one distinction that the numbers are meaningless without. Months
 * that have closed are *actual* — read from payslips that were issued, so they
 * match what left the bank. The current month is a *projection* until its run is
 * finalised: salaries as they stand today less the deductions that are a
 * function of salary, with no incentive, overtime or loss of pay, because none
 * of those are known yet. The page says which it is showing rather than
 * presenting one number and leaving the reader to guess.
 */
function ManagementDashboard() {
  const navigate = useNavigate();
  const periods = recentPeriods(24);

  const [f, setF] = useState({
    from: periods[periods.length - 12],
    to: periods[periods.length - 1],
    departmentId: '',
    level: '',
    gender: '',
  });
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const reset = () => set({
    from: periods[periods.length - 12], to: periods[periods.length - 1],
    departmentId: '', level: '', gender: '',
  });

  const departments = useQuery({ queryKey: ['departments'], queryFn: () => get('/reference/departments'), staleTime: 300_000 });

  const pay = useQuery({
    queryKey: ['payroll-analytics', f],
    queryFn: () => get('/reports/payroll-analytics', {
      from: f.from, to: f.to,
      departmentId: f.departmentId || undefined,
      level: f.level === '' ? undefined : f.level,
      gender: f.gender || undefined,
    }),
  });

  // The salary book as it stands today, which is where branch and job function
  // live. Deliberately unfiltered: it answers "where does the cost sit", and
  // narrowing it by the panel above would make the two disagree silently.
  const cost = useQuery({ queryKey: ['reports', 'cost'], queryFn: () => get('/reports/cost') });
  const headcount = useQuery({ queryKey: ['reports', 'headcount'], queryFn: () => get('/reports/headcount') });
  const turnover = useQuery({ queryKey: ['reports', 'turnover'], queryFn: () => get('/reports/turnover') });

  const d = pay.data?.data;
  const filtered = Boolean(f.departmentId || f.level !== '' || f.gender);
  const tm = d?.thisMonth;

  /* This month against the one before it. The previous month is read from the
     series rather than recomputed, so it obeys the same filters as everything
     else on the page — comparing a filtered month against an unfiltered one
     produces a number that looks like a pay rise and is a change of scope. */
  const mom = (() => {
    const series = d?.series ?? [];
    const current = tm?.finalised ? tm.actual?.gross : tm?.projection?.gross;
    const prev = series.filter((m) => m.period < tm?.period).slice(-1)[0];
    const before = prev?.actual?.gross ?? prev?.gross;
    if (current == null || !prev || !before) return null;
    return { period: prev.period, change: current - before, pct: ((current - before) / before) * 100 };
  })();
  const paidSeries = (d?.series ?? []).filter((m) => m.gross > 0);

  return (
    <>
      <PageHead
        title="Management"
        subtitle="What the company costs, and where the cost sits"
        actions={(
          <button type="button" className="btn" onClick={() => navigate('/reports')}>
            <Icon name="chart" />Full reports
          </button>
        )}
      />

      {/* ------------------------------------------------------------ filters */}
      <Card
        title="Period and scope"
        subtitle="Applies to every figure below"
        actions={filtered || f.from !== periods[periods.length - 12] ? (
          <button type="button" className="btn btn-sm" onClick={reset}><Icon name="refresh" />Reset</button>
        ) : null}
      >
        <div className="grid g-5" style={{ gap: '0 14px' }}>
          <div className="field">
            <label htmlFor="mg-from">From</label>
            <select id="mg-from" className="inp" value={f.from} onChange={(e) => set({ from: e.target.value })}>
              {periods.map((p) => <option key={p} value={p}>{monthLabel(p)}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="mg-to">To</label>
            <select id="mg-to" className="inp" value={f.to} onChange={(e) => set({ to: e.target.value })}>
              {periods.map((p) => <option key={p} value={p}>{monthLabel(p)}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="mg-dept">Department</label>
            <select id="mg-dept" className="inp" value={f.departmentId} onChange={(e) => set({ departmentId: e.target.value })}>
              <option value="">All departments</option>
              {(departments.data?.data ?? []).map((x) => <option key={x.id} value={x.id}>{departmentLabel(x, departments.data?.data ?? [])}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="mg-level">Level</label>
            <select id="mg-level" className="inp" value={f.level} onChange={(e) => set({ level: e.target.value })}>
              <option value="">All levels</option>
              <option value="0">Founder / CXO</option>
              <option value="1">Department head</option>
              <option value="2">Team lead</option>
              <option value="3">Individual contributor</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="mg-gender">Gender</label>
            <select id="mg-gender" className="inp" value={f.gender} onChange={(e) => set({ gender: e.target.value })}>
              <option value="">All</option>
              <option value="FEMALE">Female</option>
              <option value="MALE">Male</option>
              <option value="OTHER">Other</option>
              <option value="UNDISCLOSED">Undisclosed</option>
            </select>
          </div>
        </div>
        {f.from > f.to && (
          <div className="hint mt-8" style={{ color: 'var(--crit)' }}>
            <Icon name="alert" /> The start month is after the end month, so nothing will match.
          </div>
        )}
      </Card>

      {pay.isLoading ? <Loading label="Reading the payroll…" /> : d && (
        <>
          {/* -------------------------------------------------------- headline */}
          <div className="grid g-4" style={{ margin: '16px 0' }}>
            <Stat
              label={tm.finalised ? `${monthLabel(tm.period)} — final` : `${monthLabel(tm.period)} — projected`}
              icon="wallet"
              value={inrShort(tm.finalised ? tm.actual.gross : tm.projection.gross)}
              sub={tm.finalised
                ? `Approved · ${tm.actual.employees} people`
                : `Not yet run · ${tm.projection.employees} on payroll`}
            />
            <Stat
              label="Net this month"
              icon="download"
              value={inrShort(tm.finalised ? tm.actual.net : tm.projection.net)}
              sub={tm.finalised ? 'What was paid out' : 'Before incentive, overtime and LOP'}
            />
            <Stat
              label="Last 3 months"
              icon="history"
              value={inrShort(d.lastThree.gross)}
              sub={d.lastThree.periods.length
                ? d.lastThree.periods.map(monthLabel).join(' · ')
                : 'No closed months in range'}
            />
            <Stat
              label="Against last month"
              icon={mom == null ? 'wallet' : mom.change >= 0 ? 'trend' : 'trendDn'}
              value={mom == null ? '—' : `${mom.change >= 0 ? '+' : '−'}${inrShort(Math.abs(mom.change))}`}
              sub={mom == null
                ? 'No earlier month in range to compare'
                : `${mom.change >= 0 ? 'up' : 'down'} ${Math.abs(mom.pct).toFixed(1)}% on ${monthLabel(mom.period)}`}
            />
            <Stat
              label="Cost per head"
              icon="users"
              value={d.headcount ? inrShort(Math.round(tm.projection.gross / d.headcount)) : '—'}
              sub={`${d.headcount} on payroll${filtered ? ' in this scope' : ''}`}
            />
          </div>

          {/* Variance is only meaningful once the run exists. */}
          {tm.finalised && tm.variance !== null && (
            <Card style={{ marginBottom: 16 }}>
              <div className="flex items-c gap-10">
                <div className="ins-ico" style={{
                  background: tm.variance > 0 ? 'var(--warn-bg)' : 'var(--good-bg)',
                  color: tm.variance > 0 ? 'var(--warn)' : 'var(--good)',
                }}
                >
                  <Icon name={tm.variance > 0 ? 'trendUp' : 'trendDown'} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 650 }}>
                    {monthLabel(tm.period)} came in {inr(Math.abs(tm.variance))}{' '}
                    {tm.variance > 0 ? 'above' : 'below'} the projection
                  </div>
                  <div className="txt-sm muted">
                    The projection is salaries alone. The difference is incentive, overtime and reimbursement,
                    less any loss of pay.
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* ----------------------------------------------------------- trend */}
          <Card
            title="Payroll by month"
            subtitle={`${monthLabel(d.range.from)} to ${monthLabel(d.range.to)}${filtered ? ' · filtered' : ''} · gross`}
            style={{ marginBottom: 16 }}
          >
            {paidSeries.length === 0 ? (
              <Empty icon="wallet" title="No payroll in this period" sub="Nothing has been run for these months yet." />
            ) : (
              <LineChart
                labels={paidSeries.map((m) => monthLabel(m.period))}
                series={[{ name: 'Gross', data: paidSeries.map((m) => m.gross), color: token('--brand-500') }]}
                format={(v) => inrShort(v)}
                height={250}
              />
            )}
          </Card>

          <div className="grid g-23" style={{ marginBottom: 16 }}>
            <Card title="By department" subtitle={`Gross over ${monthLabel(d.range.from)}–${monthLabel(d.range.to)}`}>
              {d.byDepartment.length === 0 ? <Empty icon="briefcase" title="Nothing in scope" /> : (
                <HBars
                  rows={d.byDepartment.map((x, i) => ({
                    l: `${x.name} (${x.headcount})`, v: x.gross, c: seriesColor(i),
                  }))}
                  format={(v) => inrShort(v)}
                />
              )}
            </Card>

            <Card title="By level" subtitle="Where the cost concentrates">
              <Table
                columns={[
                  { key: 'label', label: 'Level' },
                  { key: 'headcount', label: 'People', render: (r) => r.headcount },
                  { key: 'monthlyGross', label: 'Monthly', render: (r) => inrShort(r.monthlyGross) },
                  { key: 'gross', label: 'In period', render: (r) => inrShort(r.gross) },
                  {
                    key: 'share',
                    label: 'Share',
                    render: (r) => {
                      const total = d.byLevel.reduce((s, x) => s + x.gross, 0);
                      return total ? `${Math.round((r.gross / total) * 100)}%` : '—';
                    },
                  },
                ]}
                rows={d.byLevel}
                empty="Nothing in scope"
              />
            </Card>
          </div>

          {/* Where the cost sits, cut two ways the panel above cannot cut it.
              These read the current salary book rather than the filtered
              window, so they answer "what does the company look like today"
              while everything above answers "what did these months cost". */}
          <div className="grid g-2" style={{ marginBottom: 16 }}>
            <Card title="By branch" subtitle="Monthly gross where people sit">
              <Table
                columns={[
                  { key: 'name', label: 'Branch', render: (r) => r.shortName ?? r.name },
                  { key: 'city', label: 'City' },
                  { key: 'headcount', label: 'People' },
                  { key: 'gross', label: 'Monthly', render: (r) => inrShort(r.gross) },
                  {
                    key: 'per',
                    label: 'Per head',
                    render: (r) => (r.headcount ? inrShort(Math.round(r.gross / r.headcount)) : '—'),
                  },
                ]}
                rows={(cost.data?.data?.byBranch ?? []).filter((b) => b.headcount > 0)}
                empty="No branches with anybody in them"
              />
            </Card>

            <Card
              title="By job"
              subtitle="Trainers, sales, and everything else — across departments"
            >
              <Table
                columns={[
                  { key: 'designation', label: 'Designation' },
                  { key: 'headcount', label: 'People' },
                  { key: 'gross', label: 'Monthly', render: (r) => inrShort(r.gross) },
                  {
                    key: 'per',
                    label: 'Per head',
                    render: (r) => (r.headcount ? inrShort(Math.round(r.gross / r.headcount)) : '—'),
                  },
                ]}
                /* Sorted by headcount, not by cost: with salaries still to be
                   entered every total is zero, and a table sorted by zero is a
                   table in no order at all. */
                rows={[...(cost.data?.data?.byDesignation ?? [])]
                  .sort((a, b) => b.gross - a.gross || b.headcount - a.headcount)
                  .slice(0, 12)}
                empty="Nobody has a designation recorded"
              />
              <div className="txt-sm muted mt-8">
                The twelve largest of {cost.data?.data?.byDesignation?.length ?? 0}. A designation is what the
                joining form recorded, so it groups trainers and sales staff wherever they sit.
              </div>
            </Card>
          </div>

          <div className="grid g-23" style={{ marginBottom: 16 }}>
            <Card
              title="By gender"
              subtitle="Headcount and average salary"
            >
              <Table
                columns={[
                  { key: 'label', label: 'Gender' },
                  { key: 'headcount', label: 'People', render: (r) => r.headcount },
                  { key: 'averageGross', label: 'Average', render: (r) => inr(r.averageGross) },
                  { key: 'monthlyGross', label: 'Monthly', render: (r) => inrShort(r.monthlyGross) },
                ]}
                rows={d.byGender}
                empty="Nothing in scope"
              />
              <div className="txt-sm muted mt-14">
                <Icon name="info" /> Averages, not a pay gap. A gap figure compares like with like — same role, same
                level, same tenure — and at this headcount the number would move on a single hire.
              </div>
            </Card>

            <Card title="Month by month" subtitle="The figures behind the chart" flush>
              <Table
                columns={[
                  { key: 'period', label: 'Month', render: (r) => monthLabel(r.period) },
                  { key: 'employees', label: 'People', render: (r) => r.employees },
                  { key: 'gross', label: 'Gross', render: (r) => inr(r.gross) },
                  { key: 'net', label: 'Net paid', render: (r) => inr(r.net) },
                  {
                    key: 'status',
                    label: 'Status',
                    render: (r) => (r.status
                      ? <StatusBadge value={r.status} />
                      : <span className="txt-sm muted">Not run</span>),
                  },
                ]}
                rows={[...(d.series ?? [])].reverse()}
                empty="No months in range"
              />
            </Card>
          </div>

          {/* ------------------------------------------------- company at large */}
          <div className="grid g-4" style={{ marginBottom: 16 }}>
            <Stat
              label="Headcount" icon="users"
              value={headcount.data?.data?.total ?? '—'}
              sub="Active, onboarding and serving notice"
            />
            <Stat
              label="Joined (12m)" icon="userPlus"
              value={turnover.data?.data?.joinCount ?? '—'}
              sub="Across the company"
            />
            <Stat
              label="Exited (12m)" icon="userMinus"
              value={turnover.data?.data?.exitCount ?? '—'}
              sub="Voluntary and involuntary"
            />
            <Stat
              label="Attrition" icon="trendDown"
              value={turnover.data?.data?.attritionRate != null ? `${turnover.data.data.attritionRate}%` : '—'}
              sub="Rolling twelve months"
            />
          </div>

          <UpcomingLeave days={28} title="Leave ahead" />

          <Celebrations />
        </>
      )}

      <ErrorNote error={pay.error} />
    </>
  );
}

/* --------------------------------------------------------------- admin view */

function AdminDashboard() {
  const navigate = useNavigate();

  const devices = useQuery({ queryKey: ['devices'], queryFn: () => get('/biometric/devices') });
  const unmatched = useQuery({ queryKey: ['unmatched'], queryFn: () => get('/biometric/unmatched') });
  const badges = useQuery({ queryKey: ['badges'], queryFn: () => get('/meta/badges') });
  const audit = useQuery({
    queryKey: ['audit', 'admin-home'],
    queryFn: () => get('/audit', { pageSize: 8 }),
  });
  const headcount = useQuery({ queryKey: ['reports', 'headcount'], queryFn: () => get('/reports/headcount') });

  const list = devices.data?.data ?? [];
  const live = list.filter((d) => d.isActive !== false);
  const online = live.filter((d) => (d.healthStatus || d.status) === 'ONLINE').length;
  const offline = live.length - online;
  const hc = headcount.data?.data;

  const links = [
    { to: '/biometric', icon: 'fingerprint', title: 'Biometric devices', sub: 'Terminals, sync and Cardno mapping' },
    { to: '/security', icon: 'shield', title: 'Security & data', sub: 'Roles, sessions and exports' },
    { to: '/settings', icon: 'settings', title: 'Settings', sub: 'Company structure, shifts and leave types' },
    { to: '/audit', icon: 'history', title: 'Audit trail', sub: 'Who changed what, and when' },
  ];

  return (
    <>
      <PageHead
        title="Admin Dashboard"
        subtitle="Devices, security and system configuration"
        actions={(
          <>
            <button type="button" className="btn" onClick={() => navigate('/audit')}><Icon name="history" />Audit</button>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/security')}><Icon name="shield" />Security</button>
          </>
        )}
      />

      <AttendanceDaysStrip />

      <div className="grid g-4" style={{ marginBottom: 16 }}>
        <Stat
          label="Devices online" icon="fingerprint"
          value={devices.isLoading ? '—' : online}
          unit={live.length ? `/ ${live.length}` : ''}
          sub={offline ? `${offline} offline or degraded` : 'All healthy'}
        />
        <Stat
          label="Unmatched punches" icon="alert"
          value={unmatched.data?.data?.length ?? '—'}
          sub="Card numbers not mapped to anyone"
        />
        <Stat
          label="Active headcount" icon="users"
          value={hc?.total ?? '—'}
          sub={hc ? `${hc.peopleManagers} people managers` : ''}
        />
        <Stat
          label="Open queues" icon="clipboard"
          value={(badges.data?.data?.leave ?? 0) + (badges.data?.data?.helpdesk ?? 0)}
          sub={`${badges.data?.data?.leave ?? 0} leave · ${badges.data?.data?.helpdesk ?? 0} help desk`}
        />
      </div>

      <div className="grid g-32">
        <div className="grid" style={{ gap: 16 }}>
          <Card title="Admin shortcuts" subtitle="System configuration">
            <div className="grid g-2">
              {links.map((l) => (
                <button
                  key={l.to}
                  type="button"
                  className="check-item"
                  style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => navigate(l.to)}
                >
                  <div className="ins-ico" style={{ background: 'var(--surface-3)', color: 'var(--text-2)' }}>
                    <Icon name={l.icon} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="ct">{l.title}</div>
                    <div className="cs">{l.sub}</div>
                  </div>
                  <Icon name="chevR" />
                </button>
              ))}
            </div>
          </Card>

          <Card
            title="Biometric device health"
            subtitle={live.length ? `${online} of ${live.length} online` : 'No devices registered'}
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/biometric')}>Manage<Icon name="chevR" /></button>}
          >
            {devices.isLoading ? <Loading /> : live.length === 0 ? (
              <Empty icon="fingerprint" title="No devices yet" sub="Register an eSSL terminal to start syncing punches." />
            ) : live.map((d) => {
              const tone = (d.healthStatus || d.status) === 'ONLINE' ? 'good' : (d.healthStatus || d.status) === 'DEGRADED' ? 'warn' : 'crit';
              return (
                <div key={d.id} className="flex items-c gap-10" style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div className="ins-ico" style={{ background: `var(--${tone}-bg)`, color: `var(--${tone})` }}>
                    <Icon name="fingerprint" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{d.code} · {d.branch?.shortName ?? '—'}</div>
                    <div className="txt-sm muted">{d.model}</div>
                  </div>
                  <div className="ml-auto"><StatusBadge value={d.healthStatus || d.status} /></div>
                </div>
              );
            })}
          </Card>
        </div>

        <div className="grid" style={{ gap: 16, alignContent: 'start' }}>
          <Card
            title="Recent audit events"
            subtitle="Latest configuration and security activity"
            actions={<button type="button" className="btn btn-sm" onClick={() => navigate('/audit')}>Full trail<Icon name="chevR" /></button>}
            flush
          >
            {audit.isLoading ? <Loading /> : (
              <Table
                columns={[
                  { key: 'action', label: 'Action', render: (r) => <span className="mono txt-sm">{r.action}</span> },
                  { key: 'who', label: 'Who', render: (r) => r.actor?.fullName ?? r.actorName ?? '—' },
                  { key: 'when', label: 'When', render: (r) => fmtDateShort(r.occurredAt || r.createdAt) },
                ]}
                rows={audit.data?.data ?? []}
                empty="No audit events yet"
              />
            )}
          </Card>

          {(unmatched.data?.data?.length ?? 0) > 0 && (
            <Card title="Unmatched Cardnos" subtitle="Map these under Biometric → ID mapping" style={{ borderColor: 'var(--warn)' }}>
              {(unmatched.data.data).slice(0, 6).map((u) => (
                <CheckItem
                  key={u.biometricId}
                  title={<span className="mono">{u.biometricId}</span>}
                  sub={`${u.punchCount} punches queued`}
                  actions={<button type="button" className="btn btn-xs" onClick={() => navigate('/biometric')}>Map</button>}
                />
              ))}
            </Card>
          )}
        </div>
      </div>

      <ErrorNote error={devices.error || unmatched.error || audit.error} />
    </>
  );
}

/* ------------------------------------------------------------------- selector */


export default function Dashboard() {
  const { can, user } = useAuth();
  const { workspaceView, canSwitchWorkspaceView } = useWorkspaceView();

  // ADMIN accounts pick desks from the top-bar tabs (CRM pattern).
  if (canSwitchWorkspaceView) {
    if (workspaceView === 'admin') return <AdminDashboard />;
    if (workspaceView === 'management') return <ManagementDashboard />;
    return <HrDashboard />;
  }

  // The one place a role is read rather than a permission. Leadership and HR
  // hold almost the same reads, so no permission tells them apart — and the
  // difference that matters here is what somebody came to the page to do, not
  // what they are allowed to see. HR opens the dashboard to work through a
  // queue; leadership opens it to look at the numbers.
  if (user?.role === 'ATTENDANCE_VIEWER') return <AttendanceViewerDashboard />;
  if (user?.role === 'MANAGEMENT') return <ManagementDashboard />;
  if (can('reports.read') && can('employee.read.any')) return <HrDashboard />;
  if (can('attendance.read.team')) return <ManagerDashboard />;
  return <EmployeeDashboard />;
}

/** Company attendance board only — no people ops, payroll, or approvals. */
function AttendanceViewerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const day = useAttendanceDay();
  const s = day.summary;

  return (
    <>
      <PageHead
        title={`Attendance · ${user.firstName}`}
        subtitle={`${dayOfWeek(todayISO())}, ${fmtDate(todayISO())} · view-only company attendance`}
        actions={(
          <button type="button" className="btn btn-primary" onClick={() => navigate('/attendance')}>
            <Icon name="clock" />Open attendance
          </button>
        )}
      />

      <AttendanceDaysStrip />

      <div className="grid g-4" style={{ marginBottom: 16 }}>
        <Stat
          label={day.isToday ? 'Present today' : `Present ${day.label}`}
          icon="check"
          value={s?.checkedIn ?? s?.present ?? '—'}
          unit={s ? `/ ${s.total}` : ''}
          sub={s ? `${pct(s.attendanceRate)} attendance` : ''}
        />
        <Stat label="Half day" icon="clock" value={s?.halfDay ?? '—'} sub="Under the shift threshold" />
        <Stat label="Late marks" icon="clock" value={s?.late ?? '—'} sub="Beyond the grace period" />
        <Stat label="Absent / leave" icon="alert" value={s ? (s.absent + s.onLeave) : '—'} sub={s ? `${s.onLeave} on leave` : ''} />
      </div>
    </>
  );
}
